import React, { useState } from 'react';
import { Box, Button, Typography } from '@material-ui/core';
import { ThumbUp, ThumbDown } from '@material-ui/icons';
import { useApi } from '@backstage/core-plugin-api';
import { entityFeedbackApiRef } from '@backstage-community/plugin-entity-feedback';
import { useEntity } from '@backstage/plugin-catalog-react';
import { CustomFeedbackDialog } from './CustomFeedbackDialog';

interface CustomLikeDislikeButtonsProps {
  requestResponse?: boolean;
}

export const CustomLikeDislikeButtons = (props: CustomLikeDislikeButtonsProps) => {
  const { requestResponse = true } = props;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRating, setSelectedRating] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { entity } = useEntity();
  const feedbackApi = useApi(entityFeedbackApiRef);

  const handleRating = async (rating: 'like' | 'dislike') => {
    setSelectedRating(rating);
    setIsSubmitting(true);

    try {
      const entityRef = `${entity.kind}:${entity.metadata.namespace || 'default'}/${entity.metadata.name}`;
      await feedbackApi.recordRating(entityRef, rating);

      if (rating === 'dislike' && requestResponse) {
        setDialogOpen(true);
      }
    } catch (error) {
      // Error recording rating - user can retry by clicking again
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedRating(null);
  };

  return (
    <>
      <Box 
        display="flex" 
        alignItems="center" 
        justifyContent="center" 
        style={{ gap: '16px', flexWrap: 'nowrap', width: '100%' }}
      >
        <Button
          variant={selectedRating === 'like' ? 'contained' : 'outlined'}
          color="primary"
          size="small"
          startIcon={<ThumbUp />}
          onClick={() => handleRating('like')}
          disabled={isSubmitting}
          style={{ minWidth: '80px', padding: '4px 12px' }}
        >
          Like
        </Button>
        <Button
          variant={selectedRating === 'dislike' ? 'contained' : 'outlined'}
          color="secondary"
          size="small"
          startIcon={<ThumbDown />}
          onClick={() => handleRating('dislike')}
          disabled={isSubmitting}
          style={{ minWidth: '90px', padding: '4px 12px' }}
        >
          Dislike
        </Button>
      </Box>

      {selectedRating === 'like' && (
        <Box mt={1} textAlign="center">
          <Typography variant="caption" color="textSecondary">
            Thank you for your positive feedback!
          </Typography>
        </Box>
      )}

      {dialogOpen && (
        <CustomFeedbackDialog
          entity={entity}
          open={dialogOpen}
          onClose={handleCloseDialog}
          title="Help us improve - Tell us more about your experience"
        />
      )}
    </>
  );
};